import { useState, useEffect } from 'react';
import { ticketApi } from '../../api/ticket.api';
import { aiManagementApi } from '../../api/aiManagement.api';
import { PageHeader, Badge, Skeleton } from '../../components/ui/index';
import { toPersian } from '../../utils/persianNumber';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { Robot, TrendUp, MagnifyingGlass, Users, Lightbulb } from '@phosphor-icons/react';

// Routed through Django -> the self-hosted ai_server (/generate_insight),
// same as every other AI feature in this app - never call a third-party
// API directly from the browser (this used to call api.anthropic.com
// straight from the client, exposing no key but also bypassing the
// self-hosted model entirely).
async function callAI(prompt) {
  const res = await aiManagementApi.generateInsight(prompt);
  return res.data?.text ?? '';
}

const INSIGHT_TYPES = [
  { id: 'trends',      label: 'روندها',          icon: TrendUp, color: '#233A7A' },
  { id: 'common',      label: 'مشکلات رایج',     icon: MagnifyingGlass, color: '#7c3aed' },
  { id: 'performance', label: 'عملکرد تیم',       icon: Users, color: '#059669' },
  { id: 'suggestions', label: 'پیشنهادات',        icon: Lightbulb, color: '#d97706' },
];

export default function AIInsightsDashboard() {
  const [insights, setInsights] = useState({});
  const [loading, setLoading] = useState({});
  const [stats, setStats] = useState({ total: 0, resolved: 0, pending: 0, ai: 0 });

  const loadStats = () => {
    Promise.allSettled([
      ticketApi.getSimpleUserAll(),
      ticketApi.getManagerUserAll(),
    ]).then(([r1, r2]) => {
      const all = [
        ...(r1.status === 'fulfilled' ? (Array.isArray(r1.value.data) ? r1.value.data : []) : []),
        ...(r2.status === 'fulfilled' ? (Array.isArray(r2.value.data) ? r2.value.data : []) : []),
      ];
      setStats({
        total: all.length,
        resolved: all.filter((t) => t.status === 'answered' || t.status === 'closed').length,
        pending: all.filter((t) => t.status === 'pending' || t.status === 'new').length,
        ai: all.filter((t) => t.source === 'ai').length,
      });
    });
  };

  useEffect(loadStats, []);
  useAutoRefresh(loadStats);

  const generateInsight = async (type) => {
    setLoading((p) => ({ ...p, [type]: true }));
    try {
      const prompts = {
        trends: `با توجه به اینکه تیم پشتیبانی دارای ${stats.total} تیکت با ${stats.resolved} تیکت حل‌شده و ${stats.pending} در انتظار است، روندهای اصلی را تحلیل کن.`,
        common: `در یک سامانه پشتیبانی با ${stats.total} تیکت، رایج‌ترین مشکلات و دسته‌بندی‌های احتمالی کدام‌اند؟ ۵ مورد برتر را نام ببر.`,
        performance: `با داشتن نرخ حل ${stats.resolved} از ${stats.total} تیکت، عملکرد تیم را ارزیابی کن و پیشنهادات بهبود ارائه بده.`,
        suggestions: `برای یک تیم پشتیبانی که ${stats.total} تیکت دارد و ${stats.ai} تیکت به کمک AI پاسخ داده شده، چه پیشنهادات استراتژیک برای بهبود بهره‌وری وجود دارد؟`,
      };
      const text = await callAI(prompts[type]);
      setInsights((p) => ({ ...p, [type]: text }));
    } catch {}
    setLoading((p) => ({ ...p, [type]: false }));
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="بینش‌های هوشمند AI"
        subtitle="تحلیل هوش مصنوعی از داده‌های سامانه"
        action={<span style={{ background: '#7c3aed', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Robot size={13} weight="regular" /> AI Insights</span>}
      />

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 28 }}>
        {[
          ['کل تیکت‌ها', stats.total, '#29AAE1'],
          ['حل‌شده', stats.resolved, '#059669'],
          ['در انتظار', stats.pending, '#f59e0b'],
          ['پاسخ AI', stats.ai, '#7c3aed'],
        ].map(([label, val, color]) => (
          <div key={label} style={{ background: '#fff', borderRadius: 12, border: '1px solid rgba(35,58,122,0.1)', padding: '18px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color }}>{toPersian(val)}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Insight cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 16 }}>
        {INSIGHT_TYPES.map(({ id, label, icon: Icon, color }) => (
          <div key={id} style={{ background: '#fff', borderRadius: 14, border: `1px solid ${color}20`, padding: 20, boxShadow: `0 4px 16px ${color}08` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon size={22} weight="regular" color={color} />
                <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{label}</span>
              </div>
              <button onClick={() => generateInsight(id)} disabled={loading[id]}
                style={{ padding: '6px 12px', borderRadius: 8, background: color + '12', color, border: `1px solid ${color}30`, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4 }}>
                {loading[id] ? '⟳ در حال تحلیل...' : <><Robot size={13} weight="regular" /> تحلیل AI</>}
              </button>
            </div>

            {loading[id] ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Skeleton h={13} /><Skeleton h={13} w="85%" /><Skeleton h={13} w="70%" />
              </div>
            ) : insights[id] ? (
              <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.8, margin: 0, whiteSpace: 'pre-wrap' }}>{insights[id]}</p>
            ) : (
              <p style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '20px 0', margin: 0 }}>
                برای دریافت بینش هوشمند کلیک کنید
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
